'use client';
import React, { Suspense, useMemo } from 'react';
import styled from '@benzinga/themetron';

import {
  ArticleData,
  authorTypeGenerator,
  calculateReadTime,
  Campaigns,
  getCanonicalUrl,
  getPrimaryTickers,
} from '@benzinga/article-manager';

import Hooks, { NoFirstRender } from '@benzinga/hooks';
import { checkDeviceType, DeviceType } from '@benzinga/device-utils';

import { ErrorBoundary } from '@benzinga/core-ui';

import { DateContent } from './DateContent';
import type { Mode } from '@benzinga/ads-utils';

import { truncate } from '@benzinga/utils';
import { IsUserPaywalledReturn } from '@benzinga/user-context';
import { ArticleFeaturedTicker } from '../entities';
import { NewAuthorContent } from './NewAuthorContent';
import { ArticleLayoutHeader as ArticleLayoutEditorBox } from './ArticleLayoutHeader';
import { NodeLayout } from '@benzinga/content-manager';
import { AdvertiserDisclosure } from './AdvertiserDisclosure';
import { ArticleFeaturedTickersList } from './ArticleFeaturedTickersList';
import { BzImage } from '@benzinga/image';

// const ArticleFeaturedTickersList = React.lazy(() => import('./ArticleFeaturedTickersList'));

export const ARTICLE_HEADER_BACKGROUND_IMAGE =
  'https://cdn.benzinga.com/files/images/story/2025/07/24/article-header-background-image.png';

const GoogleNewsButton = React.lazy(() => import('./GoogleNewsButton'));
const AppleNewsButton = React.lazy(() => import('./AppleNewsButton'));
const FlipboardButton = React.lazy(() => import('./FlipboardButton'));

const CallToActionForm = React.lazy(() =>
  import('@benzinga/forms-ui').then(module => {
    return { default: module.CallToActionForm };
  }),
);

export interface TaboolaSettings {
  placementMethod?: 'interval' | 'localStorage' | 'below-article';
  unitMode?: Mode;
  unitKey?: string;
}

interface ArticleLayoutHeaderProps {
  articleData: ArticleData;
  campaigns?: Campaigns | null;
  deviceType: DeviceType | null;
  featuredTickers: ArticleFeaturedTicker[];
  googleNewsUrlKey: 'benzinga' | 'benzingaIndia';
  headerRef?: (node: HTMLDivElement | null) => void;
  hideTopPanel?: boolean;
  isBot: boolean;
  isDraft: boolean;
  isEditor: boolean;
  isNotPaywalled?: boolean;
  isPaywallActive?: boolean;
  isTemplate?: boolean;
  paywall: IsUserPaywalledReturn;
  showAdvertiserDisclosure: boolean;
  showCommentButton?: boolean;
  toggleCommentsDrawer?: () => void;
  baseUrl?: string;
  layout?: NodeLayout | null;
}

export const NewArticleLayoutHeader: React.FC<ArticleLayoutHeaderProps> = React.memo(
  ({
    articleData,
    baseUrl,
    deviceType,
    featuredTickers,
    googleNewsUrlKey,
    headerRef,
    hideTopPanel = false,
    isDraft = false,
    isEditor,
    isTemplate = false,
    layout,
    showAdvertiserDisclosure = false,
  }) => {
    const isApple = Hooks.useHydrate(checkDeviceType().isApple(), false);

    const createdAt = articleData.createdAt ? new Date(articleData.createdAt).toISOString() : null;
    const contentType = articleData?.type;

    const authorName =
      articleData?.author?.firstname && articleData?.author?.lastname
        ? `${articleData?.author.firstname} ${articleData?.author.lastname}`
        : articleData?.author?.name || articleData?.name;

    const title = articleData?.isHeadline ? truncate(articleData.title ?? '', 45) : articleData.title;

    return (
      <Container $isTemplate={isTemplate} className="article-layout-main-header flex flex-col" ref={headerRef}>
        {!isTemplate && (
          <ArticleLayoutEditorBox
            articleData={articleData}
            baseUrl={baseUrl}
            isEditor={isEditor}
            isMobile={deviceType === 'mobile'}
            layout={layout}
            useNewTemplate={true}
          />
        )}
        <div className="flex relative overflow-hidden">
          {!isTemplate && (
            <BzImage
              alt=""
              fetchPriority="high"
              layout="fill"
              loader={({ src, width }) => `${src}?quality=85&auto=webp&width=${width}`}
              loaderProps={{
                width: deviceType === 'mobile' ? 450 : 1300,
              }}
              objectFit="cover"
              preload={true}
              src={ARTICLE_HEADER_BACKGROUND_IMAGE}
            />
          )}
          <div className="article-layout-main-header-content flex w-full gap-4 p-4">
            <ContentContainer className="flex flex-col w-full h-full">
              {articleData.primaryImage?.url && (
                <>
                  <BzImage
                    alt={articleData.assets?.[0]?.alt ?? 'Article image'}
                    className="absolute inset-0 w-full h-full z-0"
                    fetchPriority="high"
                    layout="fill"
                    loader={({ src, width }) => `${src}?quality=85&auto=webp&width=${width}`}
                    loaderProps={{
                      width: deviceType === 'mobile' ? 400 : 850,
                    }}
                    objectFit="cover"
                    preload={true}
                    src={articleData.primaryImage.url}
                  />
                  <div className="absolute inset-0 bg-[rgba(25,41,64,0.85)] opacity-75 z-[1]" />
                </>
              )}
              <div className="content-wrapper">
                <div className="flex flex-col w-full items-start lg:flex-row">
                  <ErrorBoundary name="article-layout-main-date-content">
                    <Suspense fallback={<div />}>
                      <DateContent
                        created={createdAt ?? ''}
                        isDraft={isDraft}
                        readTime={calculateReadTime(articleData.body)}
                      />
                    </Suspense>
                  </ErrorBoundary>
                </div>
                {!!showAdvertiserDisclosure && <AdvertiserDisclosure slug={layout?.disclosure_slug} />}

                <h1 className="article-title text-2xl md:text-4xl text-white font-normal my-4">{title}</h1>

                {!hideTopPanel && (
                  <div className="flex flex-col w-full items-start lg:flex-row">
                    <ErrorBoundary name="article-layout-main-author-content">
                      <Suspense fallback={<div />}>
                        <NewAuthorContent
                          authorLink={articleData.author?.profileUrl}
                          authorName={authorName}
                          authorType={authorTypeGenerator(
                            articleData.author?.byLine ?? '',
                            showAdvertiserDisclosure,
                            contentType,
                          )}
                          authorUid={articleData.author?.uid}
                          className="flex flex-grow"
                          isTemplate={isTemplate}
                          twitter={articleData.author?.twitter}
                        />
                      </Suspense>
                    </ErrorBoundary>
                  </div>
                )}

                {articleData.title && (
                  <div className="h-[40px] flex mt-auto ml-auto gap-2">
                    <Suspense fallback={<div className="mt-auto ml-auto" />}>
                      <NoFirstRender>
                        <FlipboardButton />
                        <GoogleNewsButton urlKey={googleNewsUrlKey} />
                        {!showAdvertiserDisclosure && isApple && <AppleNewsButton />}
                      </NoFirstRender>
                    </Suspense>
                  </div>
                )}
              </div>
            </ContentContainer>

            {Array.isArray(featuredTickers) && featuredTickers.length > 0 ? (
              <div className="hidden md:flex flex-col gap-4 w-full max-w-[450px] z-[2]">
                <Suspense fallback={<h1 className="text-white">ArticleFeaturedTickersList12345!!!</h1>}>
                  <ArticleFeaturedTickersList tickers={featuredTickers} />
                </Suspense>
              </div>
            ) : (
              <div className="hidden md:flex z-[2]">
                <React.Suspense>
                  <CallToActionForm
                    // hubspotFormId="09985891-260e-4ba5-98b8-99d74c57e1ce"
                    beehiivFormId="c03f46e3-b180-439f-8cf4-103cbf2ac567"
                    styleOption="secondary"
                    subtitle="Enter your email to get Benzinga's ultimate morning update: The PreMarket Activity Newsletter"
                    title="Beat the Market With Our Free Pre-Market Newsletter"
                  />
                </React.Suspense>
              </div>
            )}
          </div>
        </div>
      </Container>
    );
  },
);

NewArticleLayoutHeader.displayName = 'NewArticleLayoutHeader';

const Container = styled.div<{ $isTemplate?: boolean }>`
  background-color: ${({ $isTemplate }) => ($isTemplate ? '#FFFFFF' : '#192940')};

  .article-layout-main-header-content {
    max-width: 1300px;
    margin: 0 auto;
    flex-direction: column;
    align-items: flex-start;
  }

  .article-header-ticker-list {
    display: none;
  }

  .call-to-action-form-wrapper {
    display: none;
  }

  @media (min-width: 1020px) {
    .article-layout-main-header-content {
      flex-direction: row;
    }

    .article-header-ticker-list {
      display: block;
    }

    .call-to-action-form-wrapper {
      display: flex;

      .secondary-container {
        height: auto;
      }
    }
  }

  .call-to-action-container {
    .success-message-container {
      margin-top: auto;
    }
  }
`;

const ContentContainer = styled.div`
  position: relative;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow: hidden;

  .content-wrapper {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .author-name {
    color: ${({ theme }) => theme.colorPalette.white};
  }

  .key-points {
    border-radius: 0.25rem;
    margin-top: auto;

    .key-points-header {
      color: #979fac;
    }
  }

  .comments-count-button {
    padding: 0.3rem 1.25rem;
  }

  .page-share-buttons {
    button {
      border-radius: 0.25rem;
      overflow: hidden;
    }
  }

  .advertiser-disclosure-wrapper {
    display: flex;
    flex-wrap: wrap;
    margin-top: 0;

    @media (min-width: 500px) {
      margin-top: -25px;
      display: block;
    }

    button {
      background: #2b4c5a;
      padding: 0.15rem 0.5rem;
      border-radius: 0.5rem;
      color: #4ac0ee;
    }
  }
`;
